<template>
  <div class="margin-property-details-card">
    <v-card class="property-details elevation-0 pa-4">
      <v-list dense disabled>
        <h3 class="font-weight-bold mb-sm-4 mb-md-0 ml-md-5">
          Your property details
        </h3>
        <v-list-item-group class="mt-sm-3 ml-md-7">
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-property-type</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18">Property type:</span>
                <span class="font-14 font-md-18">
                  <strong>{{ propertyTypeText }}</strong></span
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-build-year</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18">Build year:</span>
                <span class="font-14 font-md-18">
                  <strong>{{ propertyDetails.BuildYear }}</strong></span
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-bedrooms</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18"
                  >Number of bedrooms:</span
                >
                <span class="font-14 font-md-18">
                  <strong>{{ numberOfBedroomsText }}&nbsp;</strong>
                </span>
                <v-icon
                  v-if="
                    propertyDetails.NumberOfBedrooms === illegalNumberOfRooms
                  "
                  color="var(--red-500)"
                  >mdi-alert-circle</v-icon
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-bathrooms</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18"
                  >Number of bathrooms:</span
                >
                <span class="font-14 font-md-18">
                  <strong>{{ numberOfBathroomsText }}&nbsp;</strong>
                </span>
                <v-icon
                  v-if="
                    propertyDetails.NumberOfBathrooms === illegalNumberOfRooms
                  "
                  color="var(--red-500)"
                  >mdi-alert-circle</v-icon
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-wall-type</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18">Wall type:</span>
                <span class="font-14 font-md-18"
                  ><strong>{{ wallTypeText }}</strong></span
                >
                <v-icon v-if="isWallTypeOther" color="var(--red-500)"
                  >mdi-alert-circle</v-icon
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-roof-type</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18">Roof type:</span>
                <span class="font-14 font-md-18"
                  ><strong>{{ roofTypeText }}</strong></span
                >
                <v-icon v-if="isRoofTypeOther" color="var(--red-500)"
                  >mdi-alert-circle</v-icon
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
      <v-btn
        text
        color="accent"
        class="text-capitalize font-weight-bold"
        @click="handleEdit"
        aria-label="Edit Property Details"
        >Edit</v-btn
      >
    </v-card>

    <!-- No bedrooms/bathrooms error message -->
    <v-alert
      v-if="showNumberOfRoomsErrorMessage"
      color="var(--red-100)"
      class="mt-4"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Sorry!</span> Records indicate this
          property has more than 8 {{ numberOfRoomsMessageText }} so
          unfortunately we can not provide a quote at this time. If this is
          incorrect, please click 'Edit' and enter the correct amount.
        </div>
      </div>
    </v-alert>

    <!-- Wall/Roof type other error message -->
    <v-alert
      v-if="isWallTypeOther || isRoofTypeOther"
      color="var(--red-100)"
      class="mt-4"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Sorry!</span>&nbsp;Please check the
          data returned for the property's wall/roof type. Click 'Edit' and
          select the correct wall/roof type from the options available.
        </div>
      </div>
    </v-alert>

    <v-alert color="var(--blue-100)" class="mt-4 note-warning">
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon>icon-bath</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Note:</span>&nbsp;The number of
          bathrooms shown above should include all bathrooms, en-suites,
          wetrooms, toilet rooms and shower rooms.
        </div>
      </div>
    </v-alert>
    <!-- Check details are correct message -->
    <v-alert
      v-if="!isWallTypeOther && !isRoofTypeOther"
      color="var(--orange-100)"
      class="mt-4 mb-0 note-warning"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--orange-500)">icon-warning-small</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Warning!</span>&nbsp;It's very
          important the property details above are correct. Click 'Edit' if you
          need to change any details.
        </div>
      </div>
    </v-alert>
  </div>
</template>

<script>
import { wordsToNumbers } from "@/common/answers";
import { ROOF_TYPE, WALL_TYPE } from "@/constants/property-detail.constant";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapActions } from "vuex";

export default {
  name: "LayoutPropertyDetailsCard",
  props: {
    propertyDetails: {
      type: Object,
      required: true,
    },
    isD2C: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      illegalNumberOfRooms: "NinePlus",
    };
  },
  computed: {
    propertyTypeText() {
      function getPropertyType(value, isD2C) {
        return isD2C
          ? {
              DetachedHouse: "Detached house",
              SemiDetachedHouse: "Semi-detached house",
              Terraced: "Terraced house",
              DormerBungalow: "Dormer bungalow",
              DetachedBungalow: "Detached bungalow",
              SemiDetachedBungalow: "Semi-detached bungalow",
              Flat: "Flat",
            }[value]
          : {
              DetachedHouse: "Detached House",
              SemiDetachedHouse: "Semi-Detached House",
              Terraced: "Terraced House",
              DormerBungalow: "Dormer Bungalow",
              DetachedBungalow: "Detached Bungalow",
              SemiDetachedBungalow: "Semi-Detached Bungalow",
              Flat: "Flat",
            }[value];
      }
      return getPropertyType(this.propertyDetails.PropertyType, this.isD2C);
    },
    roofTypeText() {
      function getRoofType(value) {
        return {
          Tile: "Tile",
          Slate: "Slate",
          FeltOnTimber: "Felt on Timber",
          Concrete: "Concrete",
          Metal: "Metal",
          Timber: "Timber",
          Asphalt: "Asphalt",
          Shingle: "Shingle",
          Stramit: "Stramit",
          ThatchFibre: "Thatch fibre",
          ThatchReed: "Thatch reed",
          Other: "Other",
        }[value];
      }
      const roofType = getRoofType(this.propertyDetails.RoofConstruction);
      if (this.isD2C) {
        return roofType + " roof";
      }
      return roofType;
    },
    wallTypeText() {
      function getWallType(value) {
        return {
          Brick: "Brick",
          Concrete: "Concrete",
          Stone: "Stone",
          Flint: "Flint",
          Timber: "Timber",
          BrickOverTimber: "Brick over Timber frame",
          Metal: "Metal",
          Other: "Other",
        }[value];
      }
      const wallType = getWallType(this.propertyDetails.WallConstruction);
      if (this.isD2C) {
        return wallType + " walls";
      }
      return wallType;
    },
    isWallTypeOther() {
      return (
        this.wallTypeText === WALL_TYPE.OTHER ||
        this.wallTypeText === WALL_TYPE.OTHER_WALLS
      );
    },
    isRoofTypeOther() {
      return (
        this.roofTypeText === ROOF_TYPE.OTHER ||
        this.roofTypeText === ROOF_TYPE.OTHER_ROOF
      );
    },
    numberOfBedroomsText() {
      return this.matchNumberWordToDisplayText(
        this.propertyDetails.NumberOfBedrooms,
        " bedrooms",
        " bedroom"
      );
    },
    numberOfBathroomsText() {
      return this.matchNumberWordToDisplayText(
        this.propertyDetails.NumberOfBathrooms,
        " bathrooms",
        " bathroom"
      );
    },
    numberOfRoomsMessageText() {
      const bedroomAnswer = this.propertyDetails.NumberOfBedrooms;
      const bathroomAnswer = this.propertyDetails.NumberOfBathrooms;

      if (
        bedroomAnswer === this.illegalNumberOfRooms &&
        bathroomAnswer === this.illegalNumberOfRooms
      ) {
        return "bedrooms and bathrooms";
      }
      if (bedroomAnswer === this.illegalNumberOfRooms) {
        return "bedrooms";
      }
      if (bathroomAnswer === this.illegalNumberOfRooms) {
        return "bathrooms";
      }
      return "";
    },
    showNumberOfRoomsErrorMessage() {
      return (
        this.propertyDetails.NumberOfBedrooms === this.illegalNumberOfRooms ||
        this.propertyDetails.NumberOfBathrooms === this.illegalNumberOfRooms
      );
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, ["setPropertyDetailsFormVisibility"]),
    handleEdit() {
      this.$emit("emitEditButtonValue", false);
      this.setPropertyDetailsFormVisibility(true);
    },
    /**
     * Match the given number word to a text to display
     * @param {string} numberWord
     * @param {string} pluralText text to append when matched number is > 1 (only apply for d2c)
     * @param {string} singularText text to append when matched number is < 1 (only apply for d2c)
     */
    matchNumberWordToDisplayText(numberWord, pluralText, singularText) {
      const number = wordsToNumbers(numberWord);
      if (this.isD2C) {
        // cover for case where value match is NinePlus or maybe more in the future
        return typeof number === "string" || number > 1
          ? number + pluralText
          : number + singularText;
      }
      return number;
    },
  },
};
</script>

<style lang="scss" scoped>
.theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
  color: $black-2;
}

.margin-property-details-card {
  margin: -3px 0 13px 0;
}

.property-details {
  background-color: $gray-light-4;
  border: solid 2px $black-2;
  position: relative;

  .v-btn {
    position: absolute;
  }

  .v-list {
    background-color: $gray-light-4;

    h3 {
      font-size: 20px;
    }

    .v-list-item__title {
      font-size: 18px;
      line-height: 20px;
    }
  }
}

.v-list-item--dense .v-list-item__icon,
.v-list--dense .v-list-item .v-list-item__icon {
  height: 20px !important;
  margin-top: 9px !important;
  margin-bottom: 0 !important;
}

.v-list-item--dense,
.v-list--dense .v-list-item {
  min-height: 32px !important;
}

@media screen and (min-width: 0) {
  .property-details {
    .v-btn {
      top: 19px;
      right: 19px;
    }

    .v-list {
      h3 {
        width: 220px;
        font-size: 18px;
      }
    }
  }
}

@media screen and (min-width: ($grid-breakpoints-sm - 1)) {
  .property-details {
    .v-btn {
      top: 22px;
      right: 36px;
    }

    .v-list {
      h3 {
        width: 100%;
      }
    }
  }
}
.layout-container {
  margin-bottom: -16px !important;
}
</style>
